/**
 * The languages the design system ships chrome copy for.
 *
 * Each entry names itself in its own language — no flags: a flag is a
 * country, not a language, and Spanish is spoken well beyond Argentina.
 * Hosts pass this list (or a subset of it) to `LanguageSelect`.
 */

/** BCP 47 tag of a language the design system ships copy for. */
export type SupportedLocaleTag = "en-US" | "es-AR";

export interface SupportedLocale {
  /** BCP 47 tag; the value a selector emits and i18next resolves against. */
  tag: SupportedLocaleTag;
  /** The language's name in that language, as shown to a reader. */
  nativeName: string;
}

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = [
  { tag: "en-US", nativeName: "English (United States)" },
  { tag: "es-AR", nativeName: "Español (Argentina)" },
];

/**
 * What the design system renders when the host has resolved no language:
 * the package is consumed as source by apps that may run no i18n runtime
 * at all, and those apps must keep rendering readable English.
 */
export const DEFAULT_LOCALE: SupportedLocaleTag = "en-US";

/** Narrows an arbitrary BCP 47 tag to a locale the package ships copy for. */
export function findSupportedLocale(
  tag: string | undefined,
): SupportedLocale | undefined {
  if (!tag) return undefined;
  const exact = SUPPORTED_LOCALES.find((locale) => locale.tag === tag);
  if (exact) return exact;
  const primarySubtag = tag.split("-")[0]?.toLowerCase();
  return SUPPORTED_LOCALES.find(
    (locale) => locale.tag.split("-")[0].toLowerCase() === primarySubtag,
  );
}
